/* ===========================================================================
 * WHERE A TOOLTIP GOES — on the side asked for if it fits, on the opposite side
 * if it does not, and never outside the window (owner, 2026-10-01: a tip in a
 * dialog was cut off at the dialog's edge — "fix this everywhere").
 *
 * Pure, so the rule is tested without a browser: the component measures, this
 * decides. Coordinates are viewport pixels, for a `position: fixed` box.
 * ======================================================================== */

export type TooltipSide = 'top' | 'bottom' | 'left' | 'right';

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

/** Room kept between the tip and its trigger, and between the tip and the window edge. */
const GAP = 6;
const MARGIN = 8;

const OPPOSITE: Record<TooltipSide, TooltipSide> = { top: 'bottom', bottom: 'top', left: 'right', right: 'left' };

function at(side: TooltipSide, t: Rect, w: number, h: number): { top: number; left: number } {
  switch (side) {
    case 'top':
      return { top: t.top - h - GAP, left: t.left + t.width / 2 - w / 2 };
    case 'bottom':
      return { top: t.top + t.height + GAP, left: t.left + t.width / 2 - w / 2 };
    case 'left':
      return { top: t.top + t.height / 2 - h / 2, left: t.left - w - GAP };
    case 'right':
      return { top: t.top + t.height / 2 - h / 2, left: t.left + t.width + GAP };
  }
}

function fits(side: TooltipSide, p: { top: number; left: number }, w: number, h: number, vw: number, vh: number): boolean {
  if (side === 'top') return p.top >= MARGIN;
  if (side === 'bottom') return p.top + h <= vh - MARGIN;
  if (side === 'left') return p.left >= MARGIN;
  return p.left + w <= vw - MARGIN;
}

export function placeTooltip(input: {
  trigger: Rect;
  width: number;
  height: number;
  side: TooltipSide;
  viewportWidth: number;
  viewportHeight: number;
}): { top: number; left: number; side: TooltipSide } {
  const { trigger, width: w, height: h, viewportWidth: vw, viewportHeight: vh } = input;
  let side = input.side;
  let p = at(side, trigger, w, h);
  if (!fits(side, p, w, h, vw, vh)) {
    const flipped = at(OPPOSITE[side], trigger, w, h);
    if (fits(OPPOSITE[side], flipped, w, h, vw, vh)) {
      side = OPPOSITE[side];
      p = flipped;
    }
  }
  // Whatever side it is on, it stays inside the window.
  const clamp = (v: number, max: number) => Math.min(Math.max(v, MARGIN), Math.max(MARGIN, max - MARGIN));
  return { top: clamp(p.top, vh - h), left: clamp(p.left, vw - w), side };
}
